import React, { useEffect, useState, useRef } from "react";
import { MapType } from "../../types/dataTypes";
import MapboxDraw from "@mapbox/mapbox-gl-draw";
import * as turf from "@turf/turf";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";
import Modal from "../Shared/Modal";
import { ControlPosition } from "maplibre-gl";
import { TEXT_DRAWING_TOOL } from "../../utils/text";

type ProlyDrawCalcElementProps = {
  map: MapType;
  control_position?: ControlPosition;
};

function ProlyDrawCalcElement({ map, control_position }: ProlyDrawCalcElementProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [area, setArea] = useState<number | null>(null);
  const isInitialized = useRef(false);

  useEffect(() => {
    if (!map.current) return;

    const mapCurrent = map.current;

    if (isInitialized.current) return;

    // Initialize Mapbox Draw with styles
    const draw = new MapboxDraw({
      displayControlsDefault: false,
      styles: [
        // ACTIVE (being drawn)
        {
          "id": "gl-draw-polygon-fill",
          "type": "fill",
          "filter": ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
          "paint": {
            "fill-color": "#D20C0C",
            "fill-outline-color": "#D20C0C",
            "fill-opacity": 0.1
          }
        },
        {
          "id": "gl-draw-polygon-stroke-active",
          "type": "line",
          "filter": ["all", ["==", "$type", "Polygon"], ["!=", "mode", "static"]],
          "layout": {
            "line-cap": "round",
            "line-join": "round"
          },
          "paint": {
            "line-color": "#D20C0C",
            "line-dasharray": [0.2, 2],
            "line-width": 2
          }
        },
        // INACTIVE (static, already drawn)
        {
          "id": "gl-draw-polygon-fill-static",
          "type": "fill",
          "filter": ["all", ["==", "$type", "Polygon"], ["==", "mode", "static"]],
          "paint": {
            "fill-color": "#404040",
            "fill-outline-color": "#404040",
            "fill-opacity": 0.1
          }
        },
        {
          "id": "gl-draw-polygon-stroke-static",
          "type": "line",
          "filter": ["all", ["==", "$type", "Polygon"], ["==", "mode", "static"]],
          "layout": {
            "line-cap": "round",
            "line-join": "round"
          },
          "paint": {
            "line-color": "#404040",
            "line-width": 2
          }
        }
      ],
      controls: {
        polygon: true,
        trash: true,
      },
    });

    mapCurrent.addControl(draw as any, control_position || "top-left");

    // Event listener for drawing creation and update
    mapCurrent.on("draw.create", updateArea);
    mapCurrent.on("draw.update", updateArea);
    mapCurrent.on("draw.delete", clearArea);

    // Event listener for mode change to update cursor
    mapCurrent.on("draw.modechange", updateCursor);

    function updateArea(e: any) {
      // Clear existing polygons
      const existingFeatures = draw.getAll();
      if (existingFeatures.features.length > 0) {
        draw.deleteAll();
      }

      // Add the new polygon and calculate its area
      const feature = e.features[0];
      if (feature) {
        const area = turf.area(feature);
        const roundedArea = Math.round(area * 100) / 100;
        setArea(roundedArea);
        setIsModalOpen(true);
      }
    }

    function clearArea() {
      setArea(null);
      setIsModalOpen(false);
    }

    function updateCursor(e: any) {
      const mode = e.mode;
      if (mode === "draw_polygon") {
        mapCurrent.getCanvas().style.cursor = "crosshair";
      } else {
        mapCurrent.getCanvas().style.cursor = "";
      }
    }

    // Mark as initialized
    isInitialized.current = true;

    return () => {
      mapCurrent.off("draw.create", updateArea);
      mapCurrent.off("draw.update", updateArea);
      mapCurrent.off("draw.delete", clearArea);
      mapCurrent.off("draw.modechange", updateCursor);
      mapCurrent.removeControl(draw as any);
    };
  }, [map, control_position]);

  return (
    <>
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        {area !== null ? (
          <p>{TEXT_DRAWING_TOOL.POLYGON_AREA} {area.toLocaleString('fr-FR')} {TEXT_DRAWING_TOOL.SQUARE_METER}</p>
        ) : (
          <p>No polygon drawn</p>
        )}
      </Modal>
    </>
  );
}

export default ProlyDrawCalcElement;